import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { GeneralSettingsType, GeneralSettingsParams } from '@/types/setting'
import { getErrorMessage } from '@/lib/utils'
import axios from '@/lib/axios'

type GeneralSettingsStateType = {
  isLoading: boolean
  response: GeneralSettingsType | null
  error: {
    status?: number
    message: string
  } | null
}

type GeneralSettingsStoreType = {
  generalSettingsState: GeneralSettingsStateType
  getGeneralSettings: () => Promise<GeneralSettingsType>
  updateGeneralSettings: (params: GeneralSettingsParams) => Promise<GeneralSettingsType>
  reset: () => void
}

const initialState: GeneralSettingsStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useGeneralSettingsStore = create<GeneralSettingsStoreType>()(
  devtools(set => ({
    generalSettingsState: initialState,
    getGeneralSettings: async () => {
      set(() => ({
        generalSettingsState: {
          ...initialState,
          isLoading: true
        }
      }))

      try {
        const { data } = await axios.get('/admin/settings/general')
        set(() => ({
          generalSettingsState: {
            ...initialState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          generalSettingsState: {
            ...initialState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        throw error
      }
    },
    updateGeneralSettings: async (params: GeneralSettingsParams) => {
      set(() => ({
        generalSettingsState: {
          ...initialState,
          isLoading: true
        }
      }))

      try {
        const { data } = await axios.patch('/admin/settings/general', params)
        set(() => ({
          generalSettingsState: {
            ...initialState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          generalSettingsState: {
            ...initialState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        throw error
      }
    },
    reset: () => {
      set(() => ({
        generalSettingsState: initialState
      }))
    }
  }))
)

export default useGeneralSettingsStore
